import { useEffect, useState } from 'react';
import {
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Gauge,
  LoaderCircle,
  PackageCheck,
} from 'lucide-react';
import { api } from '../lib/api';

type AccountSummary = {
  user:{id:string;email:string;role:string;accountStatus:string};
  plan:{
    id:'free'|'creator'|'pro';
    name:string;
    description:string;
    monthlyCredits:number;
    projectLimit:number;
    storageBytes:number;
    maxConcurrentRenderJobs:number;
    kdpExport:boolean;
    monthlyPriceUsd:number;
    annualPriceUsd:number;
    source:string;
    subscriptionStatus?:string|null;
  };
  usage:{
    projects:number;
    storageBytes:number;
    storageObjects:number;
    credits:{balance:number;lifetimeGranted:number;lifetimeUsed:number};
  };
  billing:{
    hasCustomer:boolean;
    subscription?:{
      planId:string;
      status:string;
      interval?:string|null;
      currentPeriodEnd?:string|null;
      cancelAtPeriodEnd:boolean;
    }|null;
  };
};

type Plan = AccountSummary['plan'] & {
  prices:{
    month:{label:string;checkoutAvailable:boolean};
    year:{label:string;checkoutAvailable:boolean};
  }
};

function bytes(value:number){
  if(value>=1_000_000_000)return `${(value/1_000_000_000).toFixed(1)} GB`;
  return `${(value/1_000_000).toFixed(0)} MB`;
}

export default function BillingCenter({
  onClose,
}:{
  onClose:()=>void;
}){
  const[summary,setSummary]=useState<AccountSummary|null>(null);
  const[plans,setPlans]=useState<Plan[]>([]);
  const[interval,setInterval]=useState<'month'|'year'>('month');
  const[busy,setBusy]=useState('');
  const[message,setMessage]=useState('');

  const load=async()=>{
    const[a,b]=await Promise.all([
      api.get<AccountSummary>('/api/account/summary'),
      api.get<{plans:Plan[]}>('/api/billing/plans'),
    ]);
    setSummary(a.data);setPlans(b.data.plans);
  };
  useEffect(()=>{load().catch(e=>setMessage(e instanceof Error?e.message:'Billing could not be loaded.'));},[]);

  const checkout=async(planId:string)=>{
    setBusy(planId);setMessage('');
    try{
      const{data}=await api.post<{url:string}>('/api/billing/checkout',{planId,interval});
      window.location.href=data.url;
    }catch(e){setMessage(e instanceof Error?e.message:'iCount enrollment could not open.');setBusy('')}
  };
  const portal=async()=>{
    setBusy('portal');setMessage('');
    try{
      const{data}=await api.post<{url:string}>('/api/billing/portal');
      window.location.href=data.url;
    }catch(e){setMessage(e instanceof Error?e.message:'Billing management could not open.');setBusy('')}
  };

  return <div className="overlay"><div className="modal max-w-6xl">
    <div className="row items-start"><div><span className="eyebrow">ACCOUNT & BILLING</span><h2>Plan, credits and usage.</h2><p className="muted">Entitlements are enforced by the Worker, not only by the interface.</p></div><button className="iconButton" onClick={onClose}>×</button></div>
    {!summary?<div className="empty mt-8"><LoaderCircle className="spin"/><b>Loading account…</b></div>:<>
      <div className="specGrid mt-6">
        <div className="softBox"><b>Current plan</b><strong>{summary.plan.name}</strong><small>{summary.plan.subscriptionStatus||summary.plan.source}</small></div>
        <div className="softBox"><b>AI credits</b><strong>{summary.usage.credits.balance.toLocaleString()}</strong><small>{summary.plan.monthlyCredits.toLocaleString()} plan credits / period</small></div>
        <div className="softBox"><b>Projects</b><strong>{summary.usage.projects} / {summary.plan.projectLimit}</strong><small>Current library</small></div>
        <div className="softBox"><b>Storage</b><strong>{bytes(summary.usage.storageBytes)}</strong><small>of {bytes(summary.plan.storageBytes)}</small></div>
      </div>
      <div className="row mt-7"><div><h3>Plans</h3><p className="muted text-sm">Clear USD pricing is shown here before checkout. iCount remains the secure billing and accounting provider.</p></div><div className="flex gap-2"><button className={interval==='month'?'primary':'secondary'} onClick={()=>setInterval('month')}>Monthly</button><button className={interval==='year'?'primary':'secondary'} onClick={()=>setInterval('year')}>Annual</button></div></div>
      <div className="grid md:grid-cols-3 gap-4 mt-5">{plans.map(plan=>{
        const current=summary.plan.id===plan.id;
        const pricing=plan.prices[interval];
        return <article className={`card p-5 ${current?'ring-2 ring-[#ef5b3e]':''}`} key={plan.id}>
          <div className="row"><h3>{plan.name}</h3>{current&&<span className="status">Current</span>}</div>
          <p className="muted text-sm mt-2 min-h-12">{plan.description}</p>
          <div className="billingPrice mt-4"><strong>{pricing.label}</strong>{interval==='year'&&plan.id!=='free'&&<small>{plan.id==='creator'?'Save $38 / year':'Save $98 / year'} · equivalent to two months free</small>}</div>
          <div className="space-y-2 text-sm mt-5">
            <div><CheckCircle2 size={14} className="inline mr-2"/>{plan.monthlyCredits.toLocaleString()} credits</div>
            <div><CheckCircle2 size={14} className="inline mr-2"/>{plan.projectLimit} project{plan.projectLimit===1?'':'s'}</div>
            <div><CheckCircle2 size={14} className="inline mr-2"/>{bytes(plan.storageBytes)} storage</div>
            <div><CheckCircle2 size={14} className="inline mr-2"/>{plan.kdpExport?'KDP export included':'KDP export locked'}</div>
          </div>
          {plan.id!=='free'&&!current&&<button className="primary w-full mt-5" disabled={!pricing.checkoutAvailable||!!busy} onClick={()=>checkout(plan.id)}>{busy===plan.id?<LoaderCircle size={15} className="spin"/>:<CreditCard size={15}/>}Upgrade</button>}
        </article>
      })}</div>
      {summary.billing.hasCustomer&&<button className="secondary mt-6" disabled={busy==='portal'} onClick={portal}><ExternalLink size={15}/>Open billing management</button>}
      <div className="softBox mt-5"><div className="row"><div><b>Usage model</b><p className="muted text-sm mt-1">Story/import/vision calls use fewer credits; image and 300-DPI Print Master generation use more. Failed AI calls are refunded automatically.</p></div><Gauge/></div></div>
    </>}
    {message&&<div className="errorBox mt-5">{message}</div>}
  </div></div>
}
